import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Wallet, TrendingUp, Gift } from "lucide-react";
import { useAuth } from "../lib/auth";
import { GET_DASHBOARD_DATA } from "../graphql/dashboard";
import { CardCarousel } from "../components/CardCarousel";
import { TransactionList } from "../components/TransactionList";
import { DashboardSkeleton } from "../components/SkeletonLoader";
import { GradientHeader } from "../components/GradientHeader";
import { SectionCard } from "../components/SectionCard";
import { PageContainer } from "../components/PageContainer";
import { MockCard } from "../components/MockCard";
import { parseMoney } from "../lib/money";

export const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading } = useQuery(GET_DASHBOARD_DATA, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });

  if (loading) return <DashboardSkeleton />;

  const physicalCards = (data?.physical_cards || []).map((c: any) => ({
    id: c.id,
    card_name: c.card_name || "Physical Card",
    status: c.status,
    type: "physical" as const,
    lastFour: c.card_number_last_four,
    balance: parseMoney(c.available_balance),
    expiry: c.expiry_month && c.expiry_year ? `${String(c.expiry_month).padStart(2, "0")}/${String(c.expiry_year).slice(-2)}` : undefined,
  }));

  const virtualCards = (data?.virtual_cards || []).map((c: any) => ({
    id: c.id,
    card_name: c.card_name,
    status: c.status,
    type: "virtual" as const,
    lastFour: c.card_number_last_four,
    balance: parseMoney(c.current_balance),
    limit: parseMoney(c.spending_limit),
    expiry: c.expiry_date ? `${c.expiry_date.slice(5, 7)}/${c.expiry_date.slice(2, 4)}` : undefined,
  }));

  const allCards = [...physicalCards, ...virtualCards];
  const userBalance = allCards.reduce((sum, c) => sum + c.balance, 0);
  const transactions = data?.transactions || [];
  const companyName = data?.users_by_pk?.company?.name;
  const hasCards = allCards.length > 0;
  const hasTx = transactions.length > 0;

  return (
    <PageContainer>
      <GradientHeader size="lg">
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted mb-2">
          {companyName || "Welcome back"}
        </p>
        <p className="money text-[40px] leading-none font-semibold text-ink">
          ${userBalance.toFixed(2)}
        </p>
        <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted mt-2">
          My balance
        </p>
      </GradientHeader>

      {/* Quick actions */}
      <div className="flex gap-2 px-4 mt-4">
        <button
          onClick={() => navigate("/deposit")}
          className="flex items-center justify-center gap-1.5 bg-surface rounded-xl border border-hairline px-4 py-2.5 flex-1 active:scale-[0.98] transition-transform"
        >
          <Wallet size={15} className="text-accent" />
          <span className="text-xs font-semibold text-ink">Deposit</span>
        </button>
        <button
          onClick={() => navigate("/transactions")}
          className="flex items-center justify-center gap-1.5 bg-surface rounded-xl border border-hairline px-4 py-2.5 flex-1 active:scale-[0.98] transition-transform"
        >
          <TrendingUp size={15} className="text-accent" />
          <span className="text-xs font-semibold text-ink">Activity</span>
        </button>
      </div>

      {/* Referral banner */}
      <div className="px-4 mt-4">
        <button
          onClick={() => navigate("/referral")}
          className="w-full flex items-center gap-3 bg-surface border border-hairline rounded-xl p-3.5 active:scale-[0.98] transition-transform"
        >
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center shrink-0">
            <Gift size={20} className="text-white" />
          </div>
          <div className="text-left flex-1">
            <p className="text-sm font-semibold text-ink">Invite & earn</p>
            <p className="text-[11px] text-muted">Earn when your friends deposit crypto</p>
          </div>
          <ArrowRight size={16} className="text-muted shrink-0" />
        </button>
      </div>

      {/* Cards section */}
      <div className="px-4 mt-5">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-muted mb-2.5">
          Your cards
        </p>
        {hasCards ? (
          <CardCarousel cards={allCards} />
        ) : (
          <div className="flex flex-col items-center">
            <MockCard size="lg" className="w-full" />
            <p className="text-sm text-muted mt-3 text-center px-4">
              Deposit at least <span className="money font-semibold text-ink">$25</span> to mint
              your card
            </p>
            <button
              onClick={() => navigate("/deposit")}
              className="mt-3 w-full h-11 bg-ink text-ground rounded-xl font-semibold text-sm active:scale-[0.99] transition-transform"
            >
              Make a deposit
            </button>
          </div>
        )}
      </div>

      {/* Recent transactions */}
      <div className="px-4 mt-5">
        <SectionCard title="Recent transactions">
          {hasTx ? (
            <>
              <TransactionList transactions={transactions} />
              <button
                onClick={() => navigate("/transactions")}
                className="flex items-center justify-center gap-1 w-full pt-2 text-accent text-sm font-semibold"
              >
                View all <ArrowRight size={14} />
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center text-center py-6">
              <div className="w-12 h-12 rounded-xl bg-ground border border-hairline flex items-center justify-center mb-3">
                <TrendingUp size={20} className="text-muted/60" />
              </div>
              <p className="text-sm text-muted">No transactions yet</p>
              <p className="text-xs text-muted/70 mt-1">
                Your spending history will show up here
              </p>
            </div>
          )}
        </SectionCard>
      </div>
    </PageContainer>
  );
};
